import { useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';
import { useActivityStore } from '../store/useActivityStore';
import { useLocationStore } from '../store/useLocationStore';
import { notify } from './notify';
import { getActivities } from './activityService';
import { getOrderDetail } from './orderService';
import { getListingDetail } from './listingService';
import { getBrowseAnchor } from '../hooks/useBrowseAnchor';
import { applyLocaleMigration } from './localeMigration';
import { showOrderActivityToast, showConnectionActivityToast, showListingStatusToast, AVATAR_MARKER } from '../components/activity/ActivityToast';
import type { RecipientActivity } from '../types/activity';
import { logInfo, logError, logDebug } from './loggerService';

// Injected by InitWs so the event handler can invalidate queries without
// importing the QueryClient from App.tsx (which would create a circular dep).
let queryClient: QueryClient | null = null;

export function setWsQueryClient(qc: QueryClient) {
  queryClient = qc;
}

export interface OrderWsEvent {
  type: string;
  orderId: string;
}

type OrderEventListener = (event: OrderWsEvent) => void;

const orderEventListeners = new Set<OrderEventListener>();

export function subscribeToOrderEvents(listener: OrderEventListener): () => void {
  orderEventListeners.add(listener);
  return () => {
    orderEventListeners.delete(listener);
  };
}

// Cloud: envs set VITE_WS_BASE_URL to the public WS LB origin (e.g.
//       wss://ws.staging.homeplated.com), bypassing Firebase Hosting since it doesn't proxy WebSocket upgrades.
// Local: leaves VITE_WS_BASE_URL unset, so we fall back to window.location and Vite's `/ws` proxy
//        (vite.config.ts) routes the upgrade to the BE on localhost.
// Computed lazily so module evaluation never reads window.location — if that
// access throws at import time (rare iOS WebView edge cases), the whole
// bundle dies before any ErrorBoundary can mount.
function getWsBaseUrl(): string {
  const envUrl = import.meta.env.VITE_WS_BASE_URL;
  if (envUrl) return envUrl;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const RECONNECT_MULTIPLIER = 2;

interface WsConnection {
  ws: WebSocket | null;
  reconnectDelay: number;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  intentionalClose: boolean;
}

const connections: Record<string, WsConnection> = {
  activityCount: { ws: null, reconnectDelay: INITIAL_RECONNECT_DELAY, reconnectTimeout: null, intentionalClose: false },
  events: { ws: null, reconnectDelay: INITIAL_RECONNECT_DELAY, reconnectTimeout: null, intentionalClose: false },
};

// Locale subscription state for the events socket. `subscribedLocaleId` is
// what the server currently believes we're subscribed to — cleared whenever
// the socket drops so the next `onopen` re-sends a fresh subscribe.
let subscribedLocaleId: string | null = null;
let locationStoreUnsub: (() => void) | null = null;

function sendEventsFrame(frame: object): void {
  const conn = connections.events;
  if (conn?.ws && conn.ws.readyState === WebSocket.OPEN) {
    conn.ws.send(JSON.stringify(frame));
  }
}

function subscribeToLocale(localeId: string): void {
  sendEventsFrame({ action: 'subscribe', localeId });
  subscribedLocaleId = localeId;
}

function unsubscribeFromLocale(localeId: string): void {
  sendEventsFrame({ action: 'unsubscribe', localeId });
}

// Resync the server's subscription to whatever the store currently says.
// Called from the events socket `onopen` (initial connect + every reconnect).
function syncLocaleSubscription(): void {
  subscribedLocaleId = null;
  const localeId = useLocationStore.getState().localeId;
  if (localeId) {
    subscribeToLocale(localeId);
  }
}

// Shared id so the rich order toast, the plain text fallback, and the
// count->0 dismissal all address the same toast slot (collapse, not stack).
const ACTIVITY_TOAST_ID = 'ws:activity-count';

// The activity-count socket only sends a bare integer, and the server replays
// the current count on every (re)connect. Track the last count for THIS session
// so we only toast on a genuine increase (a new activity actually arrived) and
// stay silent on the initial connect/reconnect replay of pre-existing unread.
// Reset in connect() so a fresh session re-baselines.
let lastActivityCount = 0;
let activityCountInitialized = false;

function resetActivityCountTracking() {
  lastActivityCount = 0;
  activityCountInitialized = false;
}

function getOrderMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.order as
    | Record<string, unknown>
    | undefined;
}

function getConnectionMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.connection as
    | Record<string, unknown>
    | undefined;
}

function getListingMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.listing as
    | Record<string, unknown>
    | undefined;
}

/** Plain text toast — today's behavior, used as the non-order fallback. */
function showCountFallbackToast(count: number) {
  notify.info(
    `You have ${count} unread ${count === 1 ? 'activity' : 'activities'}`,
    { id: ACTIVITY_TOAST_ID },
  );
}

/**
 * On a genuine unread increase, peek the newest unread activities and, if the
 * newest one is order-related, show the rich banner (listing thumbnail + title,
 * tap to open the sheet at that order). Otherwise fall back to the text toast.
 * Fetches through the query client so the read stays in TanStack Query's cache.
 */
async function showActivityIncreaseToast(count: number) {
  const profileId = useProfileStore.getState().activeProfile?.id;
  if (!profileId) {
    showCountFallbackToast(count);
    return;
  }

  try {
    // The 'activity' namespace is lazy-loaded (first useTranslation('activity'),
    // i.e. when the sheet opens). The toast can fire before that, so ensure the
    // namespace is in memory or t() returns the raw key. Idempotent.
    await i18n.loadNamespaces('activity');

    const activities = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['activities', profileId, 'new', 'toast-peek'],
          queryFn: () => getActivities(profileId, 'new', 5),
          staleTime: 0,
        })
      : await getActivities(profileId, 'new', 5);

    // We toast exactly once, for the activity that caused the count to rise.
    // The count channel only sends a bare integer, so we re-derive the cause:
    // the list is newest-first, so the genuinely newest activity (activities[0])
    // is the one that just arrived. Branch on ITS type — do NOT scan the list by
    // type, or an older still-unread order would outrank a brand-new connection
    // request and steal its toast.
    const newest = activities[0];
    if (newest) {
      const orderMeta = getOrderMeta(newest);
      if (typeof orderMeta?.orderId === 'string') {
        // Toast pattern — "new order": this is only a toast-worthy event for the
        // LISTER, who has an order to act on. The orderer (and orderedFor) also
        // receive this activity for their feed, but must NOT be toasted about an
        // order they just placed. Orderer-facing toasts (e.g. "order ready") are
        // delivered on the event socket (handleEvent), not this count channel.
        const viewerIsLister = (orderMeta.listerId as string | undefined) === profileId;
        if (!viewerIsLister) {
          return; // not the lister — suppress (badge already updated)
        }

        // DisplayProfileIds is set to the order's creator, so the first display
        // profile is the orderer — the "from {name}" person, with their pfp.
        const orderer = newest.displayProfiles?.[0];
        showOrderActivityToast({
          orderId: orderMeta.orderId as string,
          title:
            (orderMeta.listingTitle as string | undefined) ||
            i18n.t('listingFallback', { ns: 'activity' }),
          thumbnailUrl:
            (orderMeta.listingThumbnailUrl as string | undefined) ||
            (orderMeta.listingImageUrl as string | undefined),
          // The marker stands in for the whole person; the name comes from the
          // attribution summary so the shared UserAttribution renders it.
          subtitle: orderer?.displayName
            ? i18n.t('toast.orderFrom', { ns: 'activity', name: AVATAR_MARKER })
            : undefined,
          attribution: orderer
            ? {
                id: orderer.profileId,
                displayName: orderer.displayName,
                avatarUrl: orderer.avatarUrl,
                connectionLevel: orderer.connectionLevel,
              }
            : undefined,
          id: ACTIVITY_TOAST_ID,
          // New orders are sticky — the chef must see and act on them.
          sticky: true,
        });
        return;
      }

      // Listing status activity (live / expired). The styled toast is shown by
      // the event socket (handleEvent → showListingToast), so suppress the
      // generic count fallback here — otherwise the lister sees the white
      // "N unread activities" toast alongside the styled one.
      if (getListingMeta(newest)) {
        return;
      }

      // Connection activity (request / accepted). The recipient is always the
      // counterparty (the person who didn't act), so there's no self-toast to
      // suppress.
      if (getConnectionMeta(newest)) {
        const actor = newest.displayProfiles?.[0];
        showConnectionActivityToast({
          // message is the BE-localized headline ("Jane wants to connect").
          title: newest.message,
          avatarUrl: actor?.avatarUrl || undefined,
          id: ACTIVITY_TOAST_ID,
        });
        return;
      }
    }
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to fetch latest activity for toast',
    );
  }

  // No order/connection activity found, or the fetch failed — keep the user informed.
  showCountFallbackToast(count);
}

/**
 * Toast pattern — "order accepted" (event channel): tell the ORDERER the lister
 * accepted their order. Only the lister can accept, so the orderer is always the
 * recipient; the lister who triggered it is skipped via the order-detail party
 * shape (`orderedBy` is present only for a lister-side caller). Mirrors "ready".
 */
async function showOrderAcceptedToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (order.orderedBy) return; // caller is the lister — not their toast
    if (order.status !== 'accepted') return; // raced/stale event

    const lister = order.lister;
    const chefName = lister?.displayName || lister?.businessName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: chefName
        ? i18n.t('toast.orderAccepted', { ns: 'activity', name: AVATAR_MARKER })
        : undefined,
      attribution: lister ?? undefined,
      id: `ws:event:${orderId}`,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-accepted toast',
    );
  }
}

/**
 * Toast pattern — "order ready" (event channel): tell the ORDERER their order
 * is ready for pickup. The status transition creates no activity and doesn't
 * bump the count, so this rides the typed event socket instead. The same event
 * is also sent to the lister (who set it ready) — they must not be toasted, so
 * we gate on the order-detail party shape: `orderedBy` is only populated when
 * the caller is the lister, so its absence marks the orderer (and orderedFor).
 * Reuses the same rich toast as the new-order pattern (thumbnail + title + a
 * person sub-line), sourced from order detail since the event carries only an id.
 */
async function showOrderReadyToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (order.orderedBy) return; // caller is the lister — not their toast
    if (order.status !== 'ready_to_pickup') return; // raced/stale event

    const lister = order.lister;
    const chefName = lister?.displayName || lister?.businessName;
    // If the orderer is already on this order's in-progress page they can see it
    // is ready — don't make the toast sticky (let it auto-dismiss).
    const onOrderPage = window.location.pathname === `/orders/${orderId}`;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: chefName
        ? i18n.t(
            // Drop-off "ready" is the chef's run starting: the food is coming to
            // them, not waiting to be picked up.
            order.fulfillmentMethod === 'dropoff' ? 'toast.orderOutForDelivery' : 'toast.orderReady',
            { ns: 'activity', name: AVATAR_MARKER },
          )
        : undefined,
      attribution: lister ?? undefined,
      // Per-order id so simultaneous events for different orders stack rather
      // than collapse (matches the existing event toasts' eventScope id).
      id: `ws:event:${orderId}`,
      // "Ready for pickup" is sticky so the orderer can't miss it — unless
      // they're already viewing the order, where a normal auto-dismiss is fine.
      sticky: !onOrderPage,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-ready toast',
    );
  }
}

/**
 * Toast pattern — "mark it ready" prod (event channel): remind the LISTER that
 * an accepted order's pickup time is imminent and it isn't marked ready yet.
 * Fired by the backend pickup-timing worker (order_ready_prompt), sent ONLY to
 * the lister, so this is lister-facing — gate on the order-detail party shape
 * (`orderedBy` is populated only when the caller is the lister) and drop the
 * event if the chef already acted (status moved off `accepted`).
 *
 * Unlike the passive status toasts this is force-sticky: no timer, no countdown
 * bar, dismissable only by tapping (→ the order card), swiping, or the X — the
 * chef must acknowledge it. Tapping reuses the shared onTap (scroll-to-card, or
 * open the activity sheet focused on the order).
 */
async function showOrderReadyPromptToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (!order.orderedBy) return; // caller isn't the lister — not their prod
    if (order.status !== 'accepted') return; // chef already acted / raced event

    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: i18n.t('toast.orderReadyPrompt', { ns: 'activity' }),
      id: `ws:ready-prompt:${orderId}`,
      // Must be acknowledged — stays put even if the chef is already viewing the
      // order (that's exactly when we still want the "mark it ready" nudge).
      forceSticky: true,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-ready-prompt toast',
    );
  }
}

/**
 * Toast pattern — "confirm your order" prod (event channel): remind the ORDERER
 * that the lister pressed their confirm button first and is waiting on the
 * orderer to confirm/complete the handshake. Fired from the lister-confirm
 * handler (order_confirm_reminder). Orderer-facing — gate on the order-detail
 * party shape (`orderedBy` is populated ONLY when the caller is the lister, so
 * its ABSENCE marks the orderer) and drop the event if the orderer already
 * confirmed or the order moved off its handoff status.
 *
 * Force-sticky like the lister's ready-prompt: no timer, dismissable only by
 * tapping (→ the order, where they confirm), swiping, or the X.
 */
async function showOrderConfirmReminderToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (order.orderedBy) return; // caller is the lister — not their prod
    if (order.status !== 'ready_to_pickup') return; // raced/stale
    if (order.ordererConfirmed) return; // already confirmed — nothing to remind

    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: i18n.t('toast.orderConfirmReminder', { ns: 'activity' }),
      id: `ws:confirm-reminder:${orderId}`,
      // Must be acknowledged — stays put even if the orderer is already on the
      // order screen (that's exactly when we want the "confirm now" nudge).
      forceSticky: true,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-confirm-reminder toast',
    );
  }
}

/**
 * Toast pattern — "come get it" prod (event channel): tell the EATER that the
 * lister tapped Remind on a ready_to_pickup order and is standing there with
 * their food. Lister-initiated counterpart of the worker-fired pickup reminder.
 * Distinct from the confirm-reminder above: this one is PRE-handoff (nothing to
 * confirm yet), which is why it has its own event and its own one-shot marker.
 * Orderer-facing — gate on the order-detail party shape (`orderedBy` is
 * populated ONLY when the caller is the lister, so its ABSENCE marks the
 * orderer) and drop the event if it raced past the pickup status.
 *
 * Force-sticky like the confirm reminder: someone is actively waiting on them,
 * so it's dismissable only by tapping (→ the order), swiping, or the X.
 */
async function showOrderListerNudgeToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (order.orderedBy) return; // caller is the lister — not their prod
    if (order.status !== 'ready_to_pickup') return; // raced/stale

    const listerName = order.lister?.businessName || order.lister?.displayName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: listerName
        ? i18n.t('toast.orderListerNudge', { ns: 'activity', name: listerName })
        : i18n.t('toast.orderListerNudgeNoName', { ns: 'activity' }),
      id: `ws:lister-nudge:${orderId}`,
      // Must be acknowledged — the chef is standing there waiting.
      forceSticky: true,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-lister-nudge toast',
    );
  }
}

/**
 * Toast pattern — "customer is on their way" prod (event channel): tell the
 * LISTER that the orderer tapped "On My Way" on a ready_to_pickup pickup order
 * and is heading over. Buyer-initiated counterpart of the lister's ready-prompt.
 * Lister-facing — gate on the order-detail party shape (`orderedBy` is populated
 * ONLY when the caller is the lister) and drop the event if it raced past the
 * handoff status.
 *
 * Force-sticky like the ready-prompt: the chef must acknowledge the customer is
 * coming, dismissable only by tapping (→ the order), swiping, or the X.
 */
async function showOrderOnMyWayToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (!order.orderedBy) return; // caller isn't the lister — not their prod
    if (order.status !== 'ready_to_pickup') return; // raced/stale

    const eaterName = order.orderedFor?.businessName || order.orderedFor?.displayName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: eaterName
        ? i18n.t('toast.orderOnMyWay', { ns: 'activity', name: eaterName })
        : i18n.t('toast.orderOnMyWayNoName', { ns: 'activity' }),
      id: `ws:on-my-way:${orderId}`,
      // Must be acknowledged — stays put even if the chef is already viewing the
      // order (that's exactly when we want the "customer is coming" heads-up).
      forceSticky: true,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-on-my-way toast',
    );
  }
}

/**
 * Toast pattern — "handoff denied" (event channel): tell the LISTER the orderer
 * says they did not get their dish, which undid the lister's Handed Off. Same
 * lister gate as the on-my-way toast (`orderedBy` is populated ONLY when the
 * caller is the lister), and drop it if the order has since left the handoff
 * status.
 *
 * Force-sticky: the chef thinks this order is done, so the correction has to be
 * acknowledged.
 */
async function showOrderHandoffDeniedToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (!order.orderedBy) return; // caller isn't the lister — not their prod
    if (order.status !== 'ready_to_pickup') return; // raced/stale

    const eaterName = order.orderedFor?.businessName || order.orderedFor?.displayName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: eaterName
        ? i18n.t('toast.orderHandoffDenied', { ns: 'activity', name: eaterName })
        : i18n.t('toast.orderHandoffDeniedNoName', { ns: 'activity' }),
      // Keyed per order, so a repeat denial replaces rather than stacks.
      id: `ws:handoff-denied:${orderId}`,
      forceSticky: true,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-handoff-denied toast',
    );
  }
}

/**
 * Toast pattern — "order completed" (event channel): tell the LISTER that the
 * customer confirmed/completed their order. Mirror of the "ready" toast: the
 * same event also reaches the orderer (who triggered it), so we gate on the
 * order-detail party shape — `orderedBy` is only populated when the caller is
 * the lister, so its PRESENCE marks the lister (the inverse of the ready gate).
 */
async function showOrderCompletedToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    if (!order.orderedBy) return; // caller is the orderer — not their toast
    if (order.status !== 'completed') return; // raced/stale event

    const customer = order.orderedBy;
    const customerName = customer.displayName || customer.businessName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: customerName
        ? i18n.t('toast.orderCompleted', { ns: 'activity', name: AVATAR_MARKER })
        : undefined,
      attribution: customer,
      id: `ws:event:${orderId}`,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-completed toast',
    );
  }
}

/**
 * Toast pattern — "order cancelled / expired" (event channel): a status rich
 * toast. For an explicit cancel the handler suppresses the canceller via
 * `actorId` so only the counterparty sees it; for a system expiry there's no
 * actor, so both parties get it. When the viewer is the LISTER, `orderedBy` is
 * populated (same party-shape gate as the accepted/ready/completed toasts), so
 * we attribute the toast to the customer whose order it was; the orderer viewing
 * their own cancelled order sees the plain status line (no self-attribution).
 */
async function showOrderCanceledToast(orderId: string) {
  try {
    await i18n.loadNamespaces('activity');

    const order = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['order', orderId],
          queryFn: () => getOrderDetail(orderId),
          staleTime: 0,
        })
      : await getOrderDetail(orderId);

    const customer = order.orderedBy; // present only when the viewer is the lister
    const customerName = customer?.displayName || customer?.businessName;
    showOrderActivityToast({
      orderId,
      title: order.listing?.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: order.listing?.thumbnailUrl || order.listing?.imageUrl,
      subtitle: customerName
        ? i18n.t('toast.orderCanceledFrom', { ns: 'activity', name: AVATAR_MARKER })
        : i18n.t('toast.orderCanceled', { ns: 'activity' }),
      attribution: customer ?? undefined,
      id: `ws:event:${orderId}`,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build order-cancelled toast',
    );
  }
}

/**
 * Toast pattern — "listing live / ended" (event channel): a status-only rich
 * toast (no person) telling the LISTER their listing's window opened or closed.
 * The background window worker sends these events to the lister ONLY, so no
 * actor-gating is needed. Listing title/thumbnail sourced from listing detail
 * since the event carries only the listing id.
 */
async function showListingToast(listingId: string, kind: 'live' | 'expired') {
  try {
    await i18n.loadNamespaces('activity');

    // Same anchor + key shape as useListingDetail so this shares the detail
    // page's cache entry rather than forking an anchor-less one.
    const anchor = getBrowseAnchor();
    const listing = queryClient
      ? await queryClient.fetchQuery({
          queryKey: ['listing', listingId, anchor],
          queryFn: () => getListingDetail(listingId, anchor),
          staleTime: 0,
        })
      : await getListingDetail(listingId, anchor);

    showListingStatusToast({
      title: listing.title || i18n.t('listingFallback', { ns: 'activity' }),
      thumbnailUrl: listing.thumbnailUrl || listing.imageUrl || undefined,
      subtitle: i18n.t(kind === 'live' ? 'toast.listingLive' : 'toast.listingExpired', {
        ns: 'activity',
      }),
      id: `ws:event:${listingId}`,
    });
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to build listing-status toast',
    );
  }
}

/**
 * Handle activity count WebSocket messages.
 */
function handleActivityCount(data: string) {
  const count = parseInt(data, 10);
  if (isNaN(count)) {
    logError(new Error(`Invalid activity count: ${data}`), 'Failed to parse activity count');
    return;
  }

  logDebug(`Activity count update: ${count}`);
  useActivityStore.getState().setActivityCount(count);

  // Only the first message after connect (re)baselines without toasting; after
  // that, toast only when the count actually rises.
  const isIncrease = activityCountInitialized && count > lastActivityCount;
  lastActivityCount = count;
  activityCountInitialized = true;

  if (count === 0) {
    notify.dismiss(ACTIVITY_TOAST_ID);
    return;
  }

  if (isIncrease) {
    void showActivityIncreaseToast(count);
  }
}

/**
 * Handle event WebSocket messages.
 */
function handleEvent(data: string) {
  try {
    const event = JSON.parse(data) as { type: string; id: string; actorId?: string };
    logDebug('Event received', undefined, event);

    // Scope toasts to the originating event so the same event delivered twice
    // (e.g. on WS reconnect replay) collapses into a single toast instead of
    // stacking.
    const eventScope = `ws:event:${event.id}`;
    // i18next singleton — wsService isn't in React context, can't use the
    // useTranslation hook. The current lang is whatever i18n.changeLanguage()
    // last set; if the user flips lang mid-session, next event uses the new
    // lang, the toast that's already shown stays in the old one (no harm).
    const t = (key: string) => i18n.t(key, { ns: 'orders' });
    switch (event.type) {
      case 'payment-succeeded':
        notify.success(t('wsToast.paymentCaptured'), { id: eventScope });
        break;
      case 'payment-failed':
        notify.error(t('wsToast.paymentFailed'), { id: eventScope });
        break;
      case 'payment-authorized':
        notify.success(t('wsToast.paymentAuthorized'), { id: eventScope });
        break;
      case 'checkout-complete':
        notify.success(t('wsToast.checkoutComplete'), { id: eventScope });
        break;
      case 'checkout-expired':
        notify.info(t('wsToast.checkoutExpired'), { id: eventScope });
        break;
      case 'order_status_canceled': {
        // Suppress the canceller's own toast; the counterparty still sees it.
        const me = useProfileStore.getState().activeProfile?.id;
        if (event.actorId && event.actorId === me) break;
        void showOrderCanceledToast(event.id);
        break;
      }
      case 'order_status_expired':
        // System auto-cancel on dish expiry — no actor, so both parties get it.
        void showOrderCanceledToast(event.id);
        break;
      case 'order_status_accepted':
        // Rich toast for the orderer; gated to them inside (lister is skipped).
        void showOrderAcceptedToast(event.id);
        break;
      case 'order_status_ready_to_pickup':
        // Rich toast for the orderer; gated to them inside (lister is skipped).
        void showOrderReadyToast(event.id);
        break;
      case 'order_ready_prompt':
        // Force-sticky prod for the LISTER: an accepted order's pickup is
        // imminent and isn't marked ready. Gated to the lister inside.
        void showOrderReadyPromptToast(event.id);
        break;
      case 'order_confirm_reminder':
        // Force-sticky prod for the ORDERER: the lister confirmed first and is
        // waiting on the orderer to confirm/complete. Gated to the orderer inside.
        void showOrderConfirmReminderToast(event.id);
        break;
      case 'order_lister_nudge':
        // Force-sticky prod for the ORDERER: the lister is waiting with their
        // food and tapped Remind. Gated to the orderer inside.
        void showOrderListerNudgeToast(event.id);
        break;
      case 'order_on_my_way':
        // Force-sticky prod for the LISTER: the orderer is on their way to pick
        // up a ready_to_pickup order. Gated to the lister inside.
        void showOrderOnMyWayToast(event.id);
        break;
      case 'order_handoff_denied':
        // Force-sticky prod for the LISTER: the orderer answered their Handed
        // Off with "No, I did not get my dish", so the order is back to waiting
        // on a handoff. Gated to the lister inside.
        void showOrderHandoffDeniedToast(event.id);
        break;
      case 'order_orderer_confirmed':
        // Silent refresh for the LISTER: the orderer confirmed first (no lister
        // remind). Deliberately no toast — the shared order_* invalidation below
        // refetches listing-orders so the lister's confirm card flips to
        // "orderer confirmed, press to complete" live.
        break;
      case 'order_status_completed':
        // Rich toast for the lister; gated to them inside (orderer is skipped).
        void showOrderCompletedToast(event.id);
        break;
      case 'listing-published':
        // No toast — the user just sees the feed update. Background event.
        // Invalidate both the list and map feeds; they use separate query
        // keys, so each must be invalidated to stay in sync. `['listings']`
        // also refreshes the lister's own listing set so a newly published
        // listing pops into the home-feed breadcrumb panel live.
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: ['listings'] });
          queryClient.invalidateQueries({ queryKey: ['local-listings'] });
          queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
        }
        break;
      case 'listing_live':
        // Lister-only: their scheduled listing's window opened (→ active).
        void showListingToast(event.id, 'live');
        break;
      case 'listing_expired':
        // Lister-only: their listing's window closed (→ expired).
        void showListingToast(event.id, 'expired');
        break;
      case 'locale_migrated': {
        // Admin re-pointed our current locale to a new row. Delegates to
        // the shared reconciliation helper — same code path as the feed-
        // response `localeMigrated` marker (which is the fallback for
        // clients that missed this live event). The helper dedupes both
        // sources against each other so whichever wins the race is a
        // no-op for the other.
        const evt = event as unknown as { oldLocaleId?: string; newLocaleId?: string };
        if (evt.oldLocaleId && evt.newLocaleId) {
          void applyLocaleMigration(evt.oldLocaleId, evt.newLocaleId);
        }
        break;
      }
      default:
        // Unknown event types: log only. The previous `Event: ${type}` toast
        // was debug leakage to end users.
        if (!event.type.startsWith('order_')) {
          logDebug(`Unhandled WS event type: ${event.type}`);
        }
        break;
    }

    // Invalidate order queries so any open order page re-fetches immediately.
    if (event.type.startsWith('order_') && queryClient) {
      // event.id is the orderId for order events
      queryClient.invalidateQueries({ queryKey: ['order', event.id] });
      // Also refresh listing-orders lists (lister's listing detail page)
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      // ...the buyer's own order history (My Orders page)
      queryClient.invalidateQueries({ queryKey: ['my-orders'] });
      // ...and the owner-side listing queries, whose rows carry embedded order
      // counts. `['listings']` (prefix) covers BOTH My Kitchen
      // (`['listings', 'mine', …]`) and the home-feed lister panel
      // (`['listings']`). The panel needs it because its row visibility now
      // depends on `activeOrderCount`: an expired listing keeps its row while
      // an order is still in flight, so the transition that resolves that last
      // order is exactly what should make the row go away. Invalidating only
      // `['listings', 'mine']` left the panel showing a resolved order.
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      // ...and every surface that renders the listing's remaining portions.
      // Any order transition (reserve, abandon, cancel, expire, complete)
      // moves that count, and `['listing']` / `['local-listings']` were both
      // missing here — so an open detail page or the feed behind it kept
      // showing a portion count the server had already changed. `['listing']`
      // does NOT prefix-match `['listing-orders']` (TanStack compares keys
      // element-wise), hence the separate invalidations.
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });

      // Fan out to UI subscribers (e.g., verification-window auto-pop).
      orderEventListeners.forEach((listener) => {
        try {
          listener({ type: event.type, orderId: event.id });
        } catch (err) {
          logError(
            err instanceof Error ? err : new Error(String(err)),
            'Order event listener threw',
          );
        }
      });
    }

    // Listing status flips (live/expired) change the dish status — refresh the
    // listing detail plus every surface that renders it. event.id is the
    // listingId for these events.
    if ((event.type === 'listing_live' || event.type === 'listing_expired') && queryClient) {
      queryClient.invalidateQueries({ queryKey: ['listing', event.id] });
      // `['listings']` (prefix) covers both the home-feed lister panel's
      // `['listings']` query and My Kitchen's `['listings', 'mine', …]`, so a
      // listing going live/expired adds or removes its breadcrumb row live.
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
    }
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to parse event message',
    );
  }
}

/**
 * Create a WebSocket connection with reconnection logic.
 */
function createConnection(
  key: string,
  url: string,
  onMessage: (data: string) => void,
  onOpen?: () => void,
): void {
  const conn = connections[key];
  if (!conn) return;

  conn.intentionalClose = false;

  try {
    const ws = new WebSocket(url);

    ws.onopen = () => {
      logDebug(`WebSocket connected: ${key}`);
      conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
      onOpen?.();
    };

    ws.onmessage = (event) => {
      onMessage(event.data as string);
    };

    ws.onclose = () => {
      logDebug(`WebSocket closed: ${key}`);
      conn.ws = null;
      // Server forgets subscriptions on disconnect; clear local state so the
      // next onopen syncs fresh.
      if (key === 'events') {
        subscribedLocaleId = null;
      }

      if (!conn.intentionalClose && useAuthStore.getState().isAuthenticated) {
        scheduleReconnect(key, url, onMessage, onOpen);
      }
    };

    ws.onerror = (error) => {
      logError(new Error('WebSocket error'), `WebSocket error: ${key}`);
      ws.close();
    };

    conn.ws = ws;
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      `Failed to create WebSocket: ${key}`,
    );
    scheduleReconnect(key, url, onMessage);
  }
}

/**
 * Schedule a reconnection with exponential backoff.
 */
function scheduleReconnect(
  key: string,
  url: string,
  onMessage: (data: string) => void,
  onOpen?: () => void,
): void {
  const conn = connections[key];
  if (!conn || conn.intentionalClose) return;

  logDebug(`Reconnecting ${key} in ${conn.reconnectDelay}ms`);

  conn.reconnectTimeout = setTimeout(() => {
    conn.reconnectTimeout = null;
    createConnection(key, url, onMessage, onOpen);
  }, conn.reconnectDelay);

  conn.reconnectDelay = Math.min(
    conn.reconnectDelay * RECONNECT_MULTIPLIER,
    MAX_RECONNECT_DELAY,
  );
}

/**
 * Close a single WebSocket connection and cancel any pending reconnect.
 */
function closeConnection(key: string): void {
  const conn = connections[key];
  if (!conn) return;

  conn.intentionalClose = true;

  if (conn.reconnectTimeout) {
    clearTimeout(conn.reconnectTimeout);
    conn.reconnectTimeout = null;
  }

  if (conn.ws) {
    if (conn.ws.readyState === WebSocket.OPEN) {
      conn.ws.close();
    }
    conn.ws = null;
  }

  conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
}

/**
 * Connect to both WebSocket topics for the given profile.
 */
function connect(profileId: string): void {
  disconnectAll();

  // Fresh session: re-baseline so the initial count replay doesn't toast.
  resetActivityCountTracking();

  const wsBaseUrl = getWsBaseUrl();
  const activityUrl = `${wsBaseUrl}/ws/activity/count/${profileId}`;
  const eventsUrl = `${wsBaseUrl}/ws/event/${profileId}`;

  logDebug(`Connecting WebSockets for profile: ${profileId}`);

  createConnection('activityCount', activityUrl, handleActivityCount);
  createConnection('events', eventsUrl, handleEvent, syncLocaleSubscription);

  // React to location-store changes for the lifetime of this connection.
  // When localeId changes we send unsubscribe(old) + subscribe(new) so the
  // server's locale fan-out follows the user without a reconnect.
  locationStoreUnsub = useLocationStore.subscribe((state) => {
    const next = state.localeId;
    if (next === subscribedLocaleId) return;
    if (subscribedLocaleId) {
      unsubscribeFromLocale(subscribedLocaleId);
    }
    if (next) {
      subscribeToLocale(next);
    } else {
      subscribedLocaleId = null;
    }
  });
}

/**
 * Close all WebSocket connections.
 * Called on logout and when profile changes.
 */
export function disconnectAll(): void {
  if (locationStoreUnsub) {
    locationStoreUnsub();
    locationStoreUnsub = null;
  }
  subscribedLocaleId = null;
  closeConnection('activityCount');
  closeConnection('events');
}

/**
 * Renderless component that manages WebSocket connections.
 * Connects when authenticated with an active profile.
 * Disconnects and reconnects on profile change.
 * Disconnects on logout.
 */
export function InitWs() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Select the id only — `activeProfile` object identity changes on every
  // profile refetch even when the underlying profile is the same, which would
  // otherwise tear down and re-establish the WS connections on each refetch
  // and replay the server's initial messages (the original "triple toast"
  // root cause).
  const profileId = useProfileStore((s) => s.activeProfile?.id);

  useEffect(() => {
    if (isAuthenticated && profileId) {
      connect(profileId);
    }
    return () => disconnectAll();
  }, [isAuthenticated, profileId]);

  return null;
}
